import { useCallback, useEffect, useId, useState } from 'react'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'
import { useIntersectionObserver, useMediaQuery } from 'usehooks-ts'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem
} from '~/components/UI/shadcn/carousel'
import Fade from 'embla-carousel-fade'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '~/components/UI/shadcn/accordion'

import Image from '~/components/Helpers/Image'
import BlockVideo from '~/components/Media/components/BlockVideo'
import Panoramic from '~/components/Media/components/Panoramic'
import CTABlock from '~/components/UI/Buttons/helpers/CTABlock'
import Vepple from '~/components/Embeds/Vepple/Vepple'
import { cva } from 'class-variance-authority'
import { getEntryById } from '~/lib/contensis/misc'
import { useQuery } from '@tanstack/react-query'

export type ListProps = {
  content: {
    heading: string
    slides: Array<ListSlideProps>
    buttons?: Array<Button['data']>
    ctaButtons: boolean
  }
  layoutColour?: AllBackgroundColours
}

type ListSlideProps = {
  heading: string
  copy: string
  image: Image
  video?: Asset
  media?: {
    heading?: string
    copy?: string
    image?: Image
    veppleID?: string
    sys?: { id?: string }
  }
}

type ThumbProps = {
  image: Image
  index: number
  selected: boolean
  onClick: () => void
}

const listVariants = cva('', {
  variants: {
    border: {
      'ink-blue': 'border-blue-d-40',
      'mid-blue': 'border-blue-m-40',
      white: 'border-white/60'
    },
    fill: {
      'ink-blue': 'fill-blue-d',
      'mid-blue': 'fill-blue-m',
      white: 'fill-white'
    },
    defaultVariant: { border: 'ink-blue', fill: 'ink-blue' }
  }
})

const Thumb = ({ selected, onClick, image }: ThumbProps) => {
  return (
    <button
      onClick={onClick}
      type='button'
      className={cn(
        'min-w-l cursor-pointer transition-opacity duration-300 sm:min-w-2xl',
        `${selected ? '' : 'opacity-40'}`
      )}
    >
      <img
        className='h-l w-l sm:h-2xl aspect-square object-cover sm:w-2xl'
        src={`${
          isDevelopment && !image?.asset.sys.uri.startsWith('https')
            ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
            : ''
        }${image?.asset.sys.uri}?w=80&h=80&fit=crop&q=75&f=webp`}
        alt={image?.altText}
      />
    </button>
  )
}

const MediaSlide = ({
  slide,
  inView
}: {
  slide: ListSlideProps
  inView: boolean
}) => {
  const id = useId()
  const [, setHasLoaded] = useState(false)
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5
  })

  // console.log(slide)

  const setLoaded = useCallback(() => {
    if (inView) setHasLoaded(true)
  }, [inView, setHasLoaded])

  const mediaId = slide?.media?.sys?.id
  const { data } = useQuery({
    queryKey: ['media-entry', mediaId],
    queryFn: async () => getEntryById(mediaId || ''),
    enabled: !!mediaId
  })

  let content = null

  if (slide?.media?.veppleID) {
    // Vepple embed
    content = <Vepple id={slide?.media.veppleID} />
  } else if (slide?.media?.image || data?.image) {
    // Panoramic image
    content = (
      // <Image image={slide?.media?.image} classes='h-full w-full object-cover' />
      <Panoramic
        panoramic={{
          id: id,
          title: undefined,
          image: isDevelopment
            ? {
                asset: {
                  sys: {
                    uri: 'https://pannellum.org/images/alma.jpg'
                  }
                }
              }
            : slide?.media?.image || data?.image,
          autoRotate: undefined,
          hfov: undefined,
          pitch: undefined,
          yaw: undefined,
          className: undefined
        }}
        inView={inView}
      />
    )
  } else if (slide.video && slide.image) {
    // Video with image
    content = (
      <BlockVideo
        videoUrl={slide?.video.sys.uri}
        inView={inView}
        width={''}
        height={''}
        image={slide.image}
        onLoad={setLoaded}
        pause={!isIntersecting}
      />
    )
  } else if (slide.image) {
    // Image only
    content = <Image image={slide.image} classes='h-full w-full object-cover' />
  } else {
    // Fallback
    content = <p>No media available</p>
  }

  return (
    <CarouselItem ref={ref}>
      <div className='relative my-auto aspect-5/4'>{content}</div>
    </CarouselItem>
  )
}

const InteractiveList = ({ content, layoutColour }: ListProps) => {
  const { heading, slides, buttons, ctaButtons } = content

  const [api, setApi] = useState<CarouselApi>()
  const [thumbsApi, setThumbsApi] = useState<CarouselApi>()
  const [infoApi, setInfoApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [slidesInView, setSlidesInView] = useState<number[]>([])

  const isDesktop = useMediaQuery('(min-width: 1024px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  const borderColour = ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
    ? 'white'
    : ['chartreuse', 'white', 'light-grey'].includes(layoutColour ?? '')
      ? 'mid-blue'
      : 'ink-blue'

  const updateSlidesInView = useCallback((api: CarouselApi) => {
    if (!api) return

    setSlidesInView(slidesInView => {
      if (slidesInView.length === api.slideNodes().length) {
        api.off('slidesInView', updateSlidesInView)
      }
      const inView = api
        .slidesInView()
        .filter((index: number) => !slidesInView.includes(index))
      return slidesInView.concat(inView)
    })
  }, [])

  useEffect(() => {
    if (!api) return

    updateSlidesInView(api)

    api.on('slidesInView', updateSlidesInView)
    api.on('reInit', updateSlidesInView)
  }, [api, updateSlidesInView])

  useEffect(() => {
    if (!isDesktop) thumbsApi?.scrollTo(current)
    if (!isDesktop) infoApi?.scrollTo(current)
    if (isDesktop) api?.scrollTo(current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoApi])

  const onThumbClick = useCallback(
    (index: number) => {
      if (!api) return

      api.scrollTo(index)
      thumbsApi?.scrollTo(index)
      infoApi?.scrollTo(index)
      setCurrent(index)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, infoApi]
  )

  return (
    <div className='lg:gap-2xl max-sm:space-y-m max-lg:space-y-l lg:flex'>
      <div className='lg:space-y-l lg:basis-1/2'>
        <h2>{heading}</h2>

        {isDesktop && (
          <Accordion
            type='single'
            defaultValue={`item-${current}`}
            collapsible
            className='gap-xs space-y-m flex w-full flex-col'
          >
            {slides?.map((slide, index: Index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className={cn(
                  listVariants({
                    border: borderColour,
                    fill: null
                  }),
                  'space-y-m relative border-b-1'
                )}
              >
                <AccordionTrigger
                  className={cn(
                    'text-h3-sm sm:text-h3-md lg:text-h3-lg pb-xs-plus cursor-pointer font-bold',
                    current === index ? '[&>div>svg]:!-rotate-90' : ''
                  )}
                  onClick={() => onThumbClick(index)}
                >
                  {slide.heading}

                  <svg
                    height='1'
                    width='100%'
                    className={cn(
                      'absolute bottom-0 left-0 z-1 origin-left translate-y-[1px] scale-x-0 transition-transform duration-300 group-hover:scale-x-100 group-focus:scale-x-100',
                      current === index ? 'scale-x-100' : ''
                    )}
                  >
                    <rect
                      x='0'
                      y='0'
                      width='100%'
                      height='1'
                      className={cn(
                        listVariants({
                          border: null,
                          fill: borderColour || 'default'
                        })
                      )}
                      strokeLinecap='square'
                    />
                  </svg>
                </AccordionTrigger>
                <AccordionContent className='pb-m text-base-lg pt-0'>
                  <div className='space-y-xs-plus'>{slide.copy}</div>
                </AccordionContent>
              </AccordionItem>
            ))}
            <div className='sm:gap-xs-plus gap-x-2xs gap-y-xs flex flex-wrap justify-end'>
              {buttons && (
                <CTABlock
                  content={buttons}
                  ctaButtons={ctaButtons}
                  backgroundColour={layoutColour}
                />
              )}
            </div>
          </Accordion>
        )}
      </div>

      <Carousel
        setApi={setApi}
        plugins={[Fade()]}
        opts={{ watchDrag: false }}
        className='flex items-center lg:basis-1/2 [&>div]:w-full'
      >
        <CarouselContent>
          {slides?.map((slide: ListSlideProps, index: Index) => (
            <MediaSlide
              slide={slide}
              key={`content-${index}`}
              inView={slidesInView.indexOf(index) > -1}
            />
          ))}
        </CarouselContent>
      </Carousel>

      {!isDesktop && (
        <>
          <Carousel
            setApi={setThumbsApi}
            opts={{ align: 'center', containScroll: 'keepSnaps' }}
            className='[&>div]:-ml-xs relative overflow-hidden'
          >
            <CarouselContent className='!mx-auto max-w-min'>
              {slides?.map((slide, index: Index) => (
                <CarouselItem
                  key={`thumb-${index}`}
                  className='h-l sm:h-2xl !pl-s basis-auto'
                >
                  <Thumb
                    onClick={() => onThumbClick(index)}
                    selected={index === current}
                    index={index}
                    image={
                      // slide.panoramic ? slide.panoramic.image : slide.image
                      slide.image
                    }
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <Carousel setApi={setInfoApi} plugins={[Fade()]} className='relative'>
            <CarouselContent>
              {slides?.map((slide, index: Index) => (
                <CarouselItem key={`list-${index}`}>
                  <div className='space-y-xs pb-s border-blue-m border-b'>
                    <h3>{slide.heading}</h3>
                    <p>{slide.copy}</p>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <div className='sm:gap-xs-plus gap-x-2xs gap-y-xs flex flex-wrap'>
            {buttons && (
              <CTABlock
                content={buttons}
                ctaButtons={ctaButtons}
                backgroundColour={layoutColour}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default InteractiveList
